import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';



const AdminAuthentication = () => {
  const navigate = useNavigate();
  const { signIn, signOut, userProfile, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState({ email: 'admin@ogada.com', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTime, setLockoutTime] = useState(null);

  // Redirect if already authenticated as admin
  useEffect(() => {
    if (!authLoading && userProfile?.role === 'admin') {
      navigate('/admin-panel-dashboard');
    }
  }, [userProfile, authLoading, navigate]);

  // Handle lockout timer
  useEffect(() => {
    if (isLocked && lockoutTime) {
      const timer = setInterval(() => {
        const remaining = Math.ceil((lockoutTime - Date.now()) / 1000);
        if (remaining <= 0) {
          setIsLocked(false);
          setLockoutTime(null);
          setLoginAttempts(0);
          clearInterval(timer);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isLocked, lockoutTime]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (isLocked) {
      const remaining = Math.ceil((lockoutTime - Date.now()) / 1000);
      setError(`Account locked. Please try again in ${remaining} seconds.`);
      return;
    }

    setError('');
    setLoading(true);

    try {
      const { data, error: signInError } = await signIn(formData?.email, formData?.password);
      
      if (signInError) {
        setLoginAttempts(prev => prev + 1);
        
        if (loginAttempts + 1 >= 3) {
          setIsLocked(true);
          setLockoutTime(Date.now() + 60000); // 1 minute lockout
          setError('Too many failed attempts. Account locked for 1 minute.');
        } else {
          setError(signInError?.message || 'Invalid credentials');
        }
      } else {
        // Check if user is admin
        const { data: profile } = await supabase?.from('user_profiles')?.select('role')?.eq('id', data?.user?.id)?.single();
        
        if (profile?.role !== 'admin') {
          setError('Access denied. Admin credentials required.');
          await signOut();
        } else {
          setLoginAttempts(0);
          navigate('/admin-panel-dashboard');
        }
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const translations = {
    en: {
      title: 'Admin Access',
      subtitle: 'Secure administrator authentication',
      email: 'Admin Email',
      password: 'Admin Password',
      signIn: 'Sign In as Admin',
      securityNote: 'All admin access attempts are logged and monitored',
      features: [
        'Enhanced security with account lockout protection',
        'Session timeout warnings',
        'Audit trail logging for all admin actions',
        'Two-factor authentication support'
      ]
    },
    ko: {
      title: '관리자 액세스',
      subtitle: '보안 관리자 인증',
      email: '관리자 이메일',
      password: '관리자 비밀번호',
      signIn: '관리자로 로그인',
      securityNote: '모든 관리자 액세스 시도가 기록되고 모니터링됩니다',
      features: [
        '계정 잠금 보호 기능이 있는 강화된 보안',
        '세션 시간 초과 경고',
        '모든 관리자 작업에 대한 감사 추적 로깅',
        '2단계 인증 지원'
      ]
    }
  };

  const t = translations?.en;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Security Badge */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-600 rounded-full mb-4">
            <Icon name="Shield" size={32} color="white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{t?.title}</h1>
          <p className="text-purple-200">{t?.subtitle}</p>
        </div>

        {/* Login Form */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-white/20">
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2">
              <Icon name="AlertCircle" size={16} color="#ef4444" />
              <p className="text-sm text-red-200">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                {t?.email}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="Mail" size={18} color="#c4b5fd" />
                </div>
                <input
                  type="email"
                  value={formData?.email}
                  onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="admin@ogada.com"
                  required
                  disabled={isLocked}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                {t?.password}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="Lock" size={18} color="#c4b5fd" />
                </div>
                <input
                  type="password"
                  value={formData?.password}
                  onChange={(e) => setFormData({ ...formData, password: e?.target?.value })}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="••••••••"
                  required
                  disabled={isLocked}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || isLocked}
              className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={18} className="animate-spin" />
                  Authenticating...
                </>
              ) : (
                <>
                  <Icon name="ShieldCheck" size={18} />
                  {t?.signIn}
                </>
              )}
            </button>
          </form>

          {/* Security Features */}
          <div className="mt-6 pt-6 border-t border-white/20">
            <p className="text-xs text-purple-200 mb-3 flex items-center gap-2">
              <Icon name="Info" size={14} />
              {t?.securityNote}
            </p>
            <div className="space-y-2">
              {t?.features?.map((feature, index) => (
                <div key={index} className="flex items-start gap-2 text-xs text-purple-300">
                  <Icon name="Check" size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Login Attempts Indicator */}
        {loginAttempts > 0 && !isLocked && (
          <div className="mt-4 text-center">
            <p className="text-sm text-yellow-300">
              Failed attempts: {loginAttempts}/3
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAuthentication;